'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

import { Button, Card, CardBody, CardTitle, Col, Row } from 'reactstrap';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import dayjs from '../utils/dayjs';
import { MdMailOutline, MdOutlineCall, MdOutlineLocationOn } from 'react-icons/md';
import slugify from 'slugify';
import { Agency } from '../pages/Agencies';
import { getI18nUtils } from '../i18n/i18n-utils';
import { Dictionaries, Locale } from '../i18n/i18n-config';
import { getAgencyDisplayTitle } from '../utils/agencyBranding';
import AgencyReviewsWidget from './AgencyReviewsWidget';

const days = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 0,
};

type ClickHandler = () => void;

type Props = {
  agency: Partial<Agency>;
  job?: boolean;
  detail?: boolean;
  dictionary: Partial<Dictionaries>;
  prependBrandName?: boolean;
  className?: string;
  mySynergie?: boolean;
  openingTimes?: boolean;
  moreInfo?: boolean;
  compactView?: boolean;
  isActive?: boolean;
  handleClick?: ClickHandler;
  locale: Locale;
};
const AgencyTeaser = ({
  agency,
  job = false,
  detail,
  dictionary,
  prependBrandName = false,
  className,
  mySynergie,
  openingTimes = false,
  moreInfo = false,
  compactView = false,
  handleClick,
  isActive = false,
  locale,
}: Props): JSX.Element => {
  const { formatMessage } = getI18nUtils(dictionary);
  const { title, address1, address2, postalCode, city, telephone, email, openingHours, apps, appointmentUrl } = agency;
  const displayTitle = getAgencyDisplayTitle({ title, prependBrandName });
  const href = `/${locale}/agencies/${agency.id || agency._id}/${slugify(title as string)}`;
  const address = `Synergie,${address1},${address2 ? `${address2}` : ''}${postalCode},${city}`;
  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  const isSandYou = apps?.includes('sandyou.be');
  const agencyHasId = agency.id || agency._id;
  const [today, setToday] = useState<number | null>(null);
  const showReviewsWidget = detail && !isSandYou && !!agency.reviewWidgetScriptUrl;

  useEffect(() => {
    setToday(dayjs().day());
  }, []);

  const timetable = (day) => (
    <Row className="d-flex justify-content-end">
      <Col xs={6} sm={4}>
        {formatMessage({ id: day })}
      </Col>
      {days[day] !== 0 && days[day] !== 6 && openingHours ? (
        <>
          <Col xs={6} sm={4}>
            {`${openingHours[days[day]][0].start} - ${openingHours[days[day]][0].end}`}
          </Col>
          <Col xs={6} sm={4}>
            {`${openingHours[days[day]][1].start} - ${openingHours[days[day]][1].end}`}
          </Col>
        </>
      ) : (
        <Col xs={6} sm={8}>
          {formatMessage({ id: 'closed' })}
        </Col>
      )}
    </Row>
  );

  function agencyTeaserTitle() {
    // agency page: h1 tag
    if (detail) return <h1>{displayTitle}</h1>;
    // job page:  unclickable h2 tag
    if (job) return <h2>{displayTitle}</h2>;
    // my synergie: clickable h3
    if (mySynergie && agencyHasId) {
      return (
        <Link href={href}>
          <h3>{displayTitle}</h3>
        </Link>
      );
    }
    // all the rest: clickable h2
    if (!detail && !job && !mySynergie && agencyHasId) {
      return (
        <Link href={href}>
          <h2>{displayTitle}</h2>
        </Link>
      );
    }
    // default (if for some reason there is no agencyId)
    return <h3>{displayTitle}</h3>;
  }

  function agencyCard() {
    if (compactView) {
      return (
        <Card
          onClick={handleClick}
          className={`${isActive ? 'active' : ''} agencyTeaser compactView rounded mb-2 
          ${className || ''}`}
        >
          <CardBody>
            <div className="d-flex flex-column justify-content-between h-100">
              <div>
                <CardTitle className="mb-1">
                  <h3>{displayTitle}</h3>
                </CardTitle>
                <Row className="justify-content-start mb-1">
                  <Col xs="auto" className="pe-0 d-flex align-items-start">
                    <MdOutlineLocationOn color="#6c757d" size="20" />
                  </Col>
                  <Col className="ps-1">
                    <address>
                      {address1} {address2 ? <span>{address2}</span> : null}
                      {`${postalCode} ${city}`}
                    </address>
                  </Col>
                </Row>
              </div>
            </div>
          </CardBody>
        </Card>
      );
    }
    return (
      <Card className={`agencyTeaser p-3 ${className || ''}`}>
        <CardBody>
          <div className="d-flex flex-column justify-content-between h-100">
            <div>
              <CardTitle className="mb-3 withIcon">{agencyTeaserTitle()}</CardTitle>
              <Row className="g-3">
                <Col xs={12} lg={showReviewsWidget ? 6 : 12}>
                  <Row className="justify-content-start mb-1">
                    <Col xs="auto" className="pe-0">
                      <MdOutlineLocationOn color="#6c757d" size="20" />
                    </Col>
                    <Col>
                      <a href={googleMapsUrl} target="_blank" rel="noreferrer noopener">
                        <address>
                          {address1}
                          <br />
                          {address2 ? <span>{address2}</span> : null}
                          {`${postalCode} ${city}`}
                        </address>
                      </a>
                    </Col>
                  </Row>
                  <Row className="justify-content-start mb-1">
                    <Col xs="auto" className="pe-0">
                      <MdOutlineCall color="#6c757d" size="20" />
                    </Col>
                    <Col>
                      <a href={`tel:${telephone}`}>{telephone}</a>
                    </Col>
                  </Row>
                  <Row className="justify-content-start">
                    <Col xs="auto" className="pe-0">
                      <MdMailOutline color="#6c757d" size="20" />{' '}
                    </Col>
                    <Col>
                      <a href={`mailto:${email}`}>{email}</a>
                    </Col>
                  </Row>
                </Col>
                {showReviewsWidget && (
                  <Col xs={12} lg={6}>
                    <AgencyReviewsWidget scriptUrl={agency.reviewWidgetScriptUrl!} className="w-100 border-0" />
                  </Col>
                )}
              </Row>
            </div>
            {!isSandYou && openingTimes && openingHours && (
              <Row className="mt-4 g-3">
                <Col xs={12}>
                  <div className="hours">
                    {Object.entries(days).map(([key, value]) => (
                      <div
                        key={key}
                        className={`px-3 py-1 mb-0 rounded-2 border-0 ${value === today ? 'alert alert-success rounded' : ''}`}
                      >
                        {timetable(key)}
                      </div>
                    ))}
                    <div className="px-1 mt-3">
                      <small>{formatMessage({ id: 'opening_hours_holidays' })}</small>
                    </div>
                  </div>
                </Col>
              </Row>
            )}
            {moreInfo && (
              <Link className="d-inline-block mt-4" href={href}>
                <Button outline color="primary" size="sm">
                  {formatMessage({ id: 'agency-teaser-read-more' })}
                </Button>
              </Link>
            )}
            {(agency.id === '632dbbe826b6086467dfc97c' || agency._id === '632dbbe826b6086467dfc97c') && (
              <div className="px-1 mt-3">
                <small>{formatMessage({ id: 'agency_appointment_remark' })}</small>
              </div>
            )}
            {appointmentUrl && (
              <Link className="d-inline-block mt-4" href={appointmentUrl} target="_blank" rel="noreferrer noopener">
                <Button color="primary" size="sm">
                  {formatMessage({ id: 'agency-teaser-make-an-appointment' })}
                </Button>
              </Link>
            )}
          </div>
        </CardBody>
      </Card>
    );
  }

  return <>{agencyCard()}</>;
};
export default AgencyTeaser;
